import { Box, Stack, type Theme } from "@mui/material";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import type { ImportFieldDefinition } from "../../../../../../api/types/dataImportTypes";
import GridCellValidationError from "../../../../../common/grid/GridCellValidationError";
import type { DataImportChangeRowModel } from "./dataImportChangesGridTypes";

interface Props {
  row: DataImportChangeRowModel;
  importFieldDefinition: ImportFieldDefinition;
  highlightChanges: boolean;
}

const DataImportCsvValueGridCell = ({ row, importFieldDefinition, highlightChanges }: Props) => {
  const change = row.fieldValueChanges.find(({ key }) => key === importFieldDefinition.name);
  if (!change) {
    return null;
  }

  const { oldValue, newValue, validationError } = change;

  // We do not really use the type and configuration: values come as text from CSV
  // type={importFieldDefinition.type}
  // configuration={importFieldDefinition.configuration}
  const oldValueString = oldValue === undefined || oldValue === null ? "" : stringifyValue(oldValue);
  const newValueString = newValue === undefined || newValue === null ? "" : stringifyValue(newValue);

  if (validationError) {
    return (
      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        {newValue ? <TypographyTooltipEllipsis text={newValueString} /> : <HorizontalFill />}
        <GridCellValidationError error={validationError} />
      </Box>
    );
  }

  if (highlightChanges && oldValueString !== newValueString) {
    return (
      <Stack direction="row" spacing={0.5}>
        <TypographyTooltipEllipsis
          text={oldValueString}
          typographyProps={{ sx: (t: Theme) => ({ bgcolor: makeTintedBackgroundFromColor(t, t.palette.error.main) }) }}
        />
        <TypographyTooltipEllipsis
          text={newValueString}
          typographyProps={{
            sx: (t: Theme) => ({ bgcolor: makeTintedBackgroundFromColor(t, t.palette.success.main) }),
          }}
        />
      </Stack>
    );
  }

  if (!newValueString) {
    return null;
  }

  return <TypographyTooltipEllipsis text={newValueString} />;
};

export default DataImportCsvValueGridCell;
